import React, { useEffect, useState } from 'react';
import './SplashScreen.css';

interface SplashScreenProps {
  onAnimationComplete: () => void;
  duration?: number; // Total duration in ms before calling onAnimationComplete
}

/**
 * Animated splash screen with Bob Map logo animation
 * - Circle with "bob" pulses in from center
 * - Circle slides left
 * - "map" slides in from behind
 * - Marker bounces
 */
const SplashScreen: React.FC<SplashScreenProps> = ({
  onAnimationComplete,
  duration = 2500
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      // Small delay for fade out animation
      setTimeout(onAnimationComplete, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onAnimationComplete]);

  return (
    <div className={`splash-screen ${!isVisible ? 'fade-out' : ''}`}>
      <AnimatedLogo />
    </div>
  );
};

function AnimatedLogo() {
  return (
    <svg viewBox="0 0 60 32" className="splash-logo" xmlns="http://www.w3.org/2000/svg">
      <style>{`
        @keyframes pulseIn {
          0% { transform: translateX(14px) scale(0); opacity: 0; }
          50% { transform: translateX(14px) scale(1.1); opacity: 0.8; }
          75% { transform: translateX(14px) scale(0.95); opacity: 0.9; }
          100% { transform: translateX(14px) scale(1); opacity: 1; }
        }

        @keyframes circleSlideLeft {
          0% { transform: translateX(14px); }
          100% { transform: translateX(0); }
        }

        @keyframes mapSlideIn {
          0% { transform: translateX(-20px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }

        @keyframes markerBounce {
          0%, 100% { transform: translateY(0); }
          25% { transform: translateY(-3px); }
          50% { transform: translateY(0); }
          75% { transform: translateY(-3px); }
        }

        .circle-group {
          animation: pulseIn 0.7s ease-out forwards, circleSlideLeft 0.6s ease-out 0.7s forwards;
          transform-origin: 16px 16px;
        }

        .map-slide {
          animation: mapSlideIn 0.8s ease-out 0.7s forwards;
          opacity: 0;
        }

        .marker-bounce {
          animation: markerBounce 0.8s ease-in-out 1.5s forwards;
        }
      `}</style>

      {/* "map" en premier = derrière */}
      <text className="map-slide" x="46" y="20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="700" fill="#00aa9f">map</text>

      {/* Groupe cercle + bob qui pulse puis slide */}
      <g className="circle-group">
        {/* Cercle */}
        <circle cx="16" cy="16" r="16" fill="#00aa9f"/>

        {/* "b" gauche */}
        <text x="9" y="20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="700" fill="white">b</text>

        {/* "b" droite */}
        <text x="23" y="20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="700" fill="white">b</text>

        {/* "o" + triangle qui bounce ensemble */}
        <g className="marker-bounce">
          <text x="16" y="20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="700" fill="white">o</text>
          <path d="M13.25 19 L16 24 L18.75 19" fill="white"/>
        </g>
      </g>
    </svg>
  );
}

export default SplashScreen;
